import { createSelector } from '@reduxjs/toolkit';
import {
  type CurrentUserResponseData,
  useGetCurrentUser,
} from 'gcs-common/clients/api/entities/current-user/current-user-api';
import { calculateUserStatus } from '../../helper/userStatusHelper';
import avatarPlaceHolder from '../../img/user.svg';
import { getIsChatConnected } from '../chatConnection/chatConnectionSelectors';

type CurrentUserReduxState = {
  currentUser: {
    currentUser: CurrentUserResponseData;
  };
};

export function useGetCurrentUserHasConnections() {
  const { data: currentUser } = useGetCurrentUser();
  return currentUser?.hasConnections;
}

export const getCurrentUser = (state: CurrentUserReduxState) => state.currentUser.currentUser;
export const getCurrentUserId = (state: CurrentUserReduxState) => state.currentUser.currentUser?.id;
export const getIsAgent = (state: CurrentUserReduxState) => state.currentUser.currentUser?.isAgent || false;
export const getEmail = (state: CurrentUserReduxState) => state.currentUser.currentUser?.email;
export const getCraftsmanBusinessUnit = (state: CurrentUserReduxState) =>
  state.currentUser.currentUser?.businessUnitName;

export const getPermissions = createSelector(getCurrentUser, (currentUser) => {
  return currentUser ? currentUser.permissions || [] : undefined;
});

export const getHasPermission = (permissionToCheck: string) =>
  createSelector(getPermissions, (permissions) => {
    if (!permissions) {
      // caution: undefined is different form true/false. It means we don't know yet
      return undefined;
    }
    return permissions.includes(permissionToCheck);
  });

export const getCurrentUserRoles = createSelector(getCurrentUser, (currentUser) => currentUser?.roles || []);

export const getFeatureFlags = createSelector(getCurrentUser, (currentUser) => currentUser?.featureFlags || []);

export const getHasFeatureFlag = (featureFlag: string) =>
  createSelector(getFeatureFlags, (featureFlags) => featureFlags.includes(featureFlag));

export const getCurrentFriendlyName = createSelector(getCurrentUser, (currentUser) => currentUser?.friendlyName);

export const getCurrentUserImage = createSelector(
  getCurrentUser,
  (currentUser) => currentUser?.imageUrl || avatarPlaceHolder,
);

export const getHasCurrentUserImage = createSelector(getCurrentUser, (currentUser) => !!currentUser?.imageUrl);

export const getCurrentUserCompany = createSelector(getCurrentUser, (currentUser) => currentUser?.userCompany);

export const getCurrentUserStatus = createSelector(getCurrentUser, getIsChatConnected, (currentUser, isConnected) => {
  return calculateUserStatus(isConnected, currentUser);
});

export const getIsRegistered = createSelector(getCurrentUser, (currentUser) => {
  if (!currentUser) {
    return undefined;
  }
  return !!currentUser.registered;
});
